//#region Util Imports

import * as $ from 'jquery'
import * as _ from 'underscore'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'

//#endregion

import { LocalDictionaryFactory } from '../../api/api.module'

import { Details, DetailsScope, DetailsAccordion, DetailsAccordionSection } from '../../common/common.details'
import { DetailsParametersOptions } from '../../common/details-parameters/details-parameters.directive'
import { DetailsPopupOptions, DetailsOptions } from '../../common/details-popup/details-popup.directive'
import { GridOptions, GridTypes } from '../../common/grid/grid.controller'
import { HistorySource } from '../../common/history/history.directive.controller'
import { LoggerService } from '../../common/common.module'
import { MapOptions, Map, MapLegendItem } from '../../common/map2/map.directive.controller'
import { IMRParametersOptions, IIMRParameters, IMRParametersInitializedArgs } from '../../common/parameters'
import { IBasicParametersExtension, BasicParametersExtension, BasicParameter, IDataTypeParametersExtension } from '../../common/parameters/extensions'

import { ConfigHelper } from '../../../helpers/config-helper'
import { ConvertHelper } from '../../../helpers/convert-helper'
import { DataTypeGroupComponent } from '../../../helpers/data-type-group-component'
import { EventsNames } from '../../../helpers/events-names'
import { IUserSession } from '../../../helpers/user-session'
import { OperatorLayoutSettings } from './../../../helpers/operator-layout-settings'
import { LocalizationHelper } from '../../../helpers/localization-helper'
import { ToastHelper } from '../../../helpers/toast-helper'
import { TypeHelper } from '../../../helpers/type-helper'
import { MapHelper } from '../../../helpers/map-helper'
import { TemplateHelper } from '../../../helpers/template-helper'

import { Activity } from '../../../models/common/activity'
import { Enums } from '../../../models/common/enums'
import { OpAlarm, OpAlarmEvent } from '../../../models/dw'
import { OpDataTypeInGroup } from '../../../models/core'
import { OpMarker } from '../../../models/custom/op-map-area'

import { AlarmHelper } from '../alarms.helper'


interface AlarmDetailsScope extends DetailsScope {
    ALARM_COMMON_TAB_NAME: string
    ALARM_HISTORY_TAB_NAME: string
    ALARM_LOCATIONS_ACCORDION_NAME: string
    ALARM_MESSAGES_ACCORDION_NAME: string
    ALARM_PARAMS_ACCORDION_NAME: string

    id: number

    detailsParametersOptions: DetailsParametersOptions

    basicParametersOptions: IMRParametersOptions<IDataTypeParametersExtension>
    alarmMessageParametersOptions: IMRParametersOptions<IBasicParametersExtension>

    mapOptions: MapOptions

    sentMessagesGridOptions: GridOptions

    alarmHistoryOptions: HistorySource

}

export class AlarmDirectiveController extends Details<AlarmDetailsScope> {

    // Consts

    private get ALARM_COMMON_TAB_NAME() { return 'ALARM_COMMON_TAB_NAME' }
    private get ALARM_HISTORY_GRID_NAME() { return 'alarmsHistory' }
    private get ALARM_HISTORY_TAB_NAME() { return 'ALARM_HISTORY_TAB_NAME' }
    private get ALARM_LOCATIONS_ACCORDION_NAME() { return 'ALARM_LOCATIONS_ACCORDION_NAME' }
    private get ALARM_MESSAGES_ACCORDION_NAME() { return 'ALARM_MESSAGES_ACCORDION_NAME' }
    private get ALARM_MESSAGES_GRID_NAME() { return 'alarmsMessagesGrid' }
    private get ALARM_PARAMS_ACCORDION_NAME() { return 'ALARM_PARAMS_ACCORDION_NAME' }
    private static get LOG_STRING(): string { return '[SIMAX.Alarms.Details.Controller] - %s' }

    // Consts End


    // Members

    private referenceObject: OpAlarmEvent
    private referenceObjectId: number
    private alarmMessageParameters: IIMRParameters<IBasicParametersExtension>

    // Members End

    constructor(
        $scope: AlarmDetailsScope,
        $q: ng.IQService,
        $timeout: ng.ITimeoutService,
        logger: LoggerService,
        localDictionary: LocalDictionaryFactory,
        protected userSession: IUserSession,
        protected referenceType: Enums.ReferenceType,
        private moduleName: Enums.SimaxModuleName
    ) {
        super($scope, $timeout, $q, logger, localDictionary, OperatorLayoutSettings.AlarmPopupTabActive, userSession, referenceType)



        this.setEvents()
        this.setOptions()
        this.setMapOptions()

        this.setTemplateConsts()


        if (this.userSession.getLoggedOperator().hasPermission(Activity.ALARM_EVENT_LIST)) {
            this.configureTabs(this.getTabs())
        }
    }


    // Events

    private onOpen(options: DetailsOptions): void {
        this.$scope.detailsOpenParams = options

        this.referenceObjectId = options.referenceObjectId

        this.onClear()
        this.setHistoryOptions()
        this.showPopup()
        this.onRefresh()

    }

    private onRefresh(toRefresh: OpAlarmEvent = null): void {
        this.$scope.options.isDataLoaded = false

        const deferred: JQueryDeferred<OpAlarmEvent> = $.Deferred<OpAlarmEvent>()

        if (!toRefresh) {
            this.userSession
                .getDataProvider().AlarmEvent
                .getAlarmEvent(this.referenceObjectId)
                .then((result: OpAlarmEvent) => {

                    deferred.resolve(result)
                })
                .fail((error: any) => {
                    const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)

                    ToastHelper.showError(errorMsg || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))

                    console.warn(AlarmDirectiveController.LOG_STRING, `Unable to Load data. Error: ${errorMsg}.`)
                })
                .always(() => {
                    this.$scope.showTabs = true
                })
        } else {
            deferred.resolve(toRefresh)
        }

        deferred
            .promise()
            .then((result: OpAlarmEvent) => {
                if (result && (!this.referenceObject || this.referenceObject.idAlarmEvent == result.idAlarmEvent)) {
                    this.referenceObject = result

                    return $.when(
                        this.setBasicParameters(result),
                        this.setAlarmMessagesGridOptions(result.events),
                        this.initializedAlarmMessageParameters(result)
                    )
                }
            }).always(() => {
                this.$scope.options.isDataLoaded = true
            })
    }

    private onClear(): void {
        this.$scope.popupTitle = TypeHelper.emptyString
        this.$scope.showTabs = false

        this.referenceObject = null
        this.$scope.detailsParametersOptions = null

        this.$scope.sentMessagesGridOptions = null

    }

    private onClose(): void {
        this.hidePopup();
    }

    private onConfirmAlarm(): void {
        if (_.isNumber(this.referenceObjectId)) {

            AlarmHelper
                .confirmAlarm([this.referenceObjectId], this.userSession)
                .then((alarmEvents: OpAlarmEvent[]) => {
                    this.$scope.$root.$broadcast(EventsNames.ALARM_APPROVED, 1)
                    this.$scope.$root.$broadcast(EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__CONFIRMED, this.moduleName))

                    this.onRefresh(_.first(alarmEvents))
                })
        }
    }

    // Events End


    // Setup

    getAccordions() {
        const accordions: DetailsAccordion[] = []
        let items: DetailsAccordionSection[] = []

        items = []

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.ALARM_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.AlarmParameters)) {
            items.push({
                name: this.ALARM_PARAMS_ACCORDION_NAME,
                showInTab: this.ALARM_COMMON_TAB_NAME,
                showStatus: true,
                title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.BasicParameters),
                visible: true,

                canEdit: false,
            })
        }

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.ALARM_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.AlarmEvents)) {
            items.push({
                name: this.ALARM_MESSAGES_ACCORDION_NAME,
                showInTab: this.ALARM_COMMON_TAB_NAME,
                showStatus: true,
                title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.SentMessages),
                visible: true,

                canEdit: false,
                status: () => LocalizationHelper.getLocalizedText(LocalizationHelper.Text.SentMessages) + ': ' + (this.referenceObject.events ? this.referenceObject.events.length : 0),
            })
        }

        accordions.push(new DetailsAccordion(this.$scope, this.$timeout, OperatorLayoutSettings.AlarmAccordionsSettings, 0, items, true, (component: DevExpress.ui.dxAccordion) => {
            this.$timeout(() => {
                component.expandItem(1)
            })
        }))

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.ALARM_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.AlarmMap)) {
            items = []

            items.push({
                name: this.ALARM_LOCATIONS_ACCORDION_NAME,
                showInTab: this.ALARM_COMMON_TAB_NAME,
                showStatus: false,
                title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Map),
                visible: true,

                canEdit: false,
            })

            accordions.push(new DetailsAccordion(this.$scope, this.$timeout, OperatorLayoutSettings.AlarmGroupsDetailsAccordions, 1, items))
        }

        return accordions
    }

    private getActions(): DevExpress.ui.dxButtonOptions[] {
        const operations: DevExpress.ui.dxButtonOptions[] = []

        if (this.userSession.getLoggedOperator().hasPermission(Activity.ALARMS_CONFIRMATION_ENABLED)) {

            operations.push({
                disabled: false,
                template: TemplateHelper.getIconWithText(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Submit), ConfigHelper.Icon.ALARM_DONE, this.userSession),
                hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Submit),
                width: '100%',

                onClick: (e: any) => this.onConfirmAlarm()
            })
        }

        return operations
    }

    public getTabs(): any[] {
        const tabs: any[] = []

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.ALARM_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.AlarmParameters)
            || !this.userSession.getLoggedOperator().hasPermission(Activity.ALARM_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.AlarmMap)
            || !this.userSession.getLoggedOperator().hasPermission(Activity.ALARM_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.AlarmEvents)
        ) {
            tabs.push({
                template: TemplateHelper.getContextMenuItem(
                    LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Common),
                    ConfigHelper.Icon.HOME,
                    this.userSession
                ),
                name: this.ALARM_COMMON_TAB_NAME
            })
        }

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.ALARM_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.AlarmHistory)) {
            tabs.push({
                template: TemplateHelper.getContextMenuItem(
                    LocalizationHelper.getLocalizedText(LocalizationHelper.Text.History),
                    ConfigHelper.Icon.HISTORY,
                    this.userSession
                ),
                name: this.ALARM_HISTORY_TAB_NAME
            })
        }

        return tabs
    }

    private setAlarmMessagesGridOptions(messages: OpAlarm[]): void {
        this.$scope.sentMessagesGridOptions = null

        this.$timeout(() => {
            this.$scope.sentMessagesGridOptions = {
                addCustomFilters: true,
                columnsDictionary: AlarmHelper.getAlarmMessagesGridColumns(),
                dataSource: messages,
                name: this.ALARM_MESSAGES_GRID_NAME,
                idReferenceType: Enums.ReferenceType.IdAlarm, // Using the old reference type for alarms
                type: GridTypes.NO_SEARCH_BAR_SMALL,

                allowColumnReordering: !this.userSession
                    .getLoggedOperator()
                    .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.AlarmEvents),

                buttonsOptions: {
                    enabled: false,
                },
            }
        }, 100)
    }

    private initializedAlarmMessageParameters(alarm: OpAlarmEvent): void {
        if (alarm != null && alarm.alarmMessage != null) {
            const messageValue: BasicParameter = {
                caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AlarmMessage),
                value: alarm.alarmMessage.toString(),

                isHTML: true
            }

            if (this.alarmMessageParameters != null) {
                this.alarmMessageParameters.extension.setParameters([messageValue])
            } else {
                this.$scope.alarmMessageParametersOptions = {
                    extension: new BasicParametersExtension({
                        parameters: [messageValue]
                    }),

                    showEmpty: true,

                    onInitialized: (e: IMRParametersInitializedArgs<IBasicParametersExtension>) => {
                        this.alarmMessageParameters = e.component
                    }
                }
            }
        }
    }

    private setBasicParameters(alarm: OpAlarmEvent): JQueryPromise<void> {
        const def: JQueryDeferred<void> = $.Deferred<void>()

        DataTypeGroupComponent
            .getDataTypeInGroupByModule(
                this.userSession.getDataProvider(),
                Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__PANEL_PARAMS_TO_DISPLAY,
                this.moduleName
            )
            .then((dataTypeInGroup: OpDataTypeInGroup[]) => {
                this.$scope.detailsParametersOptions = {
                    dataTypes: dataTypeInGroup,
                    referenceType: this.referenceType,
                    referenceObject: alarm,
                    useDynamicValues: true,
                    onDataLoaded: () => {
                        def.resolve()
                    }
                }
            })
            .catch(() => {
                def.reject()
            })

        if (this.isAccordionHidden(this.ALARM_PARAMS_ACCORDION_NAME)) {
            def.resolve()
        }

        return def.promise()
    }

    private setEvents(): void {
        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__OPEN_DETAILS_POPUP, this.moduleName) + this.$scope.id,
            (event: ng.IAngularEvent, ...args: any[]) => {
                this.onOpen(args[3])
            }
        )

        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__CLOSE_DETAILS_POPUP, this.moduleName) + this.$scope.id,
            () => this.onClose()
        )
    }


    private setOptions(): void {
        this.$scope.options = <DetailsPopupOptions>{
            id: this.$scope.id,

            actions: this.getActions(),
            closeEventName: EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__CLOSE_DETAILS_POPUP, this.moduleName),
            openEventName: EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__OPEN_DETAILS_POPUP, this.moduleName),
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DETAILS, this.moduleName),

            getAccordionDataMethod: () => this.getAccordionData(),
            getAccordionSelectionMethod: () => this.getAccordionSelection(),
            updateAccordionMethod: (names: any[]) => this.updateAccordions(names)
        }
    }

    private setHistoryOptions(): void {
        this.$scope.alarmHistoryOptions = {
            dataMethodName: null, // DO USUNIĘCIA
            id: null, // DO USUNIĘCIA
            name: this.ALARM_HISTORY_GRID_NAME,
            referenceType: Enums.ReferenceType.IdAlarmEvent,
            referenceValue: this.referenceObjectId,


            allowColumnReordering: !this.userSession
                .getLoggedOperator()
                .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.AlarmHistory),
        }
    }

    private setMapOptions(): void {
        MapHelper
            .getDefaultOptions(this.userSession.getDataProvider())
            .then((mapOptions: MapOptions) => {
                mapOptions.hideCloseButton = true
                mapOptions.hideCounter = true

                mapOptions.onMapInit = (map: Map) => {
                    if (this.referenceObject.location != null) {
                        const marker: OpMarker = MapHelper.opLocationToOpMarker(this.referenceObject.location)
                        if (marker != null) {
                            map.setMarkers([marker], true)
                        }
                    }
                }

                MapHelper.getLegendItems(this.userSession.getDataProvider(), Enums.ReferenceType.IdLocation)
                    .then((items: MapLegendItem[]) => {
                        mapOptions.legendItems = items
                    })
                    .always(() => {
                        this.$scope.mapOptions = mapOptions
                    })
            })
    }

    private setTemplateConsts(): void {
        this.$scope.ALARM_COMMON_TAB_NAME = this.ALARM_COMMON_TAB_NAME
        this.$scope.ALARM_HISTORY_TAB_NAME = this.ALARM_HISTORY_TAB_NAME
        this.$scope.ALARM_LOCATIONS_ACCORDION_NAME = this.ALARM_LOCATIONS_ACCORDION_NAME
        this.$scope.ALARM_MESSAGES_ACCORDION_NAME = this.ALARM_MESSAGES_ACCORDION_NAME
        this.$scope.ALARM_PARAMS_ACCORDION_NAME = this.ALARM_PARAMS_ACCORDION_NAME

    }

    // Setup End

    resetData() { this.onClear() }//DO USUNIECIA!!! - zastąpić onClear
    loadData(id: number, objects: any[]) { this.onOpen(null) }//DO USUNIECIA!!! - zastąpić onOpen
    updateData(result: any) { this.onRefresh(result) }//DO USUNIECIA!!! - zastąpić onRefresh
}